import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  Loader2,
  RotateCcw,
  SkipForward,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  isOpenTaskStatus,
  STRATEGY_SCORE_LABELS,
  taskOriginDescription,
  taskTypeLabel,
  type NetworkingTaskRow,
  type NetworkingTaskStatus,
} from '../../lib/networkingTasks';
import { ModalBody, ModalFooter, ModalHeader, ModalOverlay, ModalPanel } from '../Common/ModalShell';
import TaskOriginBadge from '../Tasks/TaskOriginBadge';
import { TRANSCRIPT_REVIEW_STEPS } from './TranscriptReviewTaskPanel';

const STATUS_LABELS: Record<NetworkingTaskStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  done: 'Done',
  skipped: 'Skipped',
  canceled: 'Canceled',
};

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface TranscriptTaskDetailModalProps {
  task: NetworkingTaskRow | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => void;
  onRequestStatusChange: (task: NetworkingTaskRow, nextStatus: NetworkingTaskStatus) => void;
}

const TranscriptTaskDetailModal: React.FC<TranscriptTaskDetailModalProps> = ({
  task,
  isOpen,
  onClose,
  onUpdated,
  onRequestStatusChange,
}) => {
  const [busy, setBusy] = useState(false);
  const [strategyScore, setStrategyScore] = useState<number | ''>('');

  useEffect(() => {
    if (!task) return;
    setStrategyScore(
      task.strategy_score != null && task.strategy_score >= 1 && task.strategy_score <= 5
        ? task.strategy_score
        : '',
    );
  }, [task]);

  if (!isOpen || !task) return null;

  const open = isOpenTaskStatus(task.status);
  const isReview = task.task_type === 'transcript_review';
  const showSubscriberLink =
    task.subscriber_id && !task.contact_email.includes('@engine.local');

  const saveStrategyScore = async (score: number | null) => {
    if (!isReview) return;
    setBusy(true);
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ strategy_score: score, updated_at: new Date().toISOString() })
        .eq('id', task.id);
      if (error) throw error;
      onUpdated();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to save score');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} closeOnBackdropClick>
      <ModalPanel maxWidthClass="max-w-lg" titleId="transcript-task-detail-title">
        <ModalHeader title={task.title} onClose={onClose} titleId="transcript-task-detail-title" />
        <ModalBody className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <TaskOriginBadge task={task} />
            <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
              {taskTypeLabel(task.task_type)}
            </span>
            <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 capitalize">
              {STATUS_LABELS[task.status] ?? task.status}
            </span>
          </div>

          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-gray-500 dark:text-gray-400">Due</dt>
            <dd className="text-gray-900 dark:text-white">{formatWhen(task.due_at)}</dd>

            <dt className="text-gray-500 dark:text-gray-400">Origin</dt>
            <dd className="text-gray-900 dark:text-white">{taskOriginDescription(task)}</dd>

            {task.completed_at ? (
              <>
                <dt className="text-gray-500 dark:text-gray-400">Completed</dt>
                <dd className="text-gray-900 dark:text-white">{formatWhen(task.completed_at)}</dd>
              </>
            ) : null}

            {showSubscriberLink ? (
              <>
                <dt className="text-gray-500 dark:text-gray-400">Contact</dt>
                <dd>
                  <Link
                    to={`/subscribers/${task.subscriber_id}`}
                    className="text-emerald-600 dark:text-emerald-400 hover:underline"
                  >
                    {task.contact_email}
                  </Link>
                </dd>
              </>
            ) : null}
          </dl>

          {task.notes?.trim() ? (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                Notes
              </h3>
              <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-3">
                {task.notes.trim()}
              </p>
            </div>
          ) : null}

          {isReview ? (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Review checklist
              </h3>
              <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {TRANSCRIPT_REVIEW_STEPS.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              <div className="mt-4">
                <label
                  htmlFor="task-detail-strategy-score"
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Strategy fit <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <select
                  id="task-detail-strategy-score"
                  value={strategyScore}
                  disabled={busy}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const next = raw ? Number(raw) : null;
                    setStrategyScore(raw ? Number(raw) : '');
                    void saveStrategyScore(next);
                  }}
                  className="text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-1.5 disabled:opacity-50 max-w-xs"
                >
                  <option value="">Not scored yet</option>
                  {[1, 2, 3, 4, 5].map((score) => (
                    <option key={score} value={score}>
                      {score} — {STRATEGY_SCORE_LABELS[score]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}

          {task.strategy_score != null && !isReview ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Strategy fit: {task.strategy_score} — {STRATEGY_SCORE_LABELS[task.strategy_score]}
            </p>
          ) : null}
        </ModalBody>
        <ModalFooter className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Close
          </button>
          <div className="flex flex-wrap items-center gap-2">
            {!open ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => onRequestStatusChange(task, 'pending')}
                title="Reopen — return this task to your open queue"
                aria-label="Reopen — return this task to your open queue"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Reopen
              </button>
            ) : (
              <>
                {task.status === 'pending' ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onRequestStatusChange(task, 'in_progress')}
                    title="Start — mark this task in progress"
                    aria-label="Start — mark this task in progress"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-sky-300 dark:border-sky-700 text-sky-800 dark:text-sky-200 hover:bg-sky-50 dark:hover:bg-sky-950/40 disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Circle className="w-4 h-4" />}
                    Start
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onRequestStatusChange(task, 'done')}
                  title="Done — mark complete; this follow-up is finished"
                  aria-label="Done — mark complete; this follow-up is finished"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Done
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onRequestStatusChange(task, 'skipped')}
                  title="Skip — not actionable; remove from your open queue"
                  aria-label="Skip — not actionable; remove from your open queue"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  <SkipForward className="w-4 h-4" />
                  Skip
                </button>
              </>
            )}
          </div>
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );
};

export default TranscriptTaskDetailModal;
